import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import type { SvgIconProps } from '@material-ui/core/SvgIcon'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import classnames from 'classnames'
import React from 'react'
import type { IconButtonProps } from '@material-ui/core/IconButton'
import { default as IconButton } from '@material-ui/core/IconButton'
import type { StyleClasses } from '../../styles'
import PopupMenu from '../PopupMenu'
import MenuItem from '../MenuItem'
import Typography from '../Typography'
import { NEXT_MENU_PLACEMENT, useNextMenuStyles } from '../Menu'

export interface ActionButtonProps extends IconButtonProps {
  classes?: StyleClasses<typeof useStyles>
  className?: string
  actions: Action[]
  disabled?: boolean
  button?: React.ReactNode
  /**
   * Opt the dropdown into the estimate-redesign look and placement (ENG-1919/ENG-1921): the
   * prototype's rounded card with inset rows, dropped 6px *below* the trigger with their right
   * edges aligned instead of Material's default of covering it. Only the `estimateNext` surfaces
   * pass this; everywhere else keeps the standard menu.
   */
  nextMenu?: boolean
  onActionClick: (event: React.MouseEvent<HTMLElement>, actionKey: string, actionPayload?: any) => void
}

export interface Action {
  key: string

  /**
   * Text of the MenuItem
   */
  label: string
  sublabel?: string

  scary?: boolean

  payload?: any

  /**
   * Icon for the MenuItem
   */
  icon?: React.ComponentType<SvgIconProps>
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  root: {},
  button: {
    padding: theme.spacing(1)
  },
  menuItem: {
    display: 'flex',
    alignItems: 'flex-start'
  },
  divider: {
    padding: 0,
    height: 0,
    minHeight: 0,
    paddingBottom: theme.spacing(),
    marginBottom: theme.spacing(),
    borderBottom: `1px solid ${theme.palette.grey[300]}`
  },
  // The `nextMenu` divider: the prototype's inset hairline, sitting inside the list's 6px padding
  // rather than running the full width of the card.
  dividerNext: {
    paddingBottom: theme.spacing(0.75),
    marginBottom: theme.spacing(0.75),
    marginLeft: theme.spacing(0.75),
    marginRight: theme.spacing(0.75),
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P94}`
  },
  scary: {
    color: theme.palette.error.main
  }
}))

function ActionButton({ disabled, actions, className, onActionClick, button, nextMenu, ...props }: ActionButtonProps) {
  const classes = useStyles(props)
  const nextClasses = useNextMenuStyles()
  const { classes: _classProps, ...baseIconProps } = props

  // PopupMenu runs its own `classes` prop through makeStyles, so these merge into the `menu`/`list`
  // class names it puts on the Menu's paper and list — no change needed there.
  const nextMenuProps = nextMenu
    ? {
        ...NEXT_MENU_PLACEMENT,
        classes: { menu: classnames(nextClasses.paper, nextClasses.paperBelow), list: nextClasses.list }
      }
    : {}

  return (
    <PopupMenu
      className={classnames(classes.root, className)}
      {...nextMenuProps}
      component={
        React.isValidElement(button) ? (
          button
        ) : (
          <IconButton
            className={classes.button}
            color="inherit"
            disabled={disabled}
            disableRipple={true}
            onClick={(ev) => ev.stopPropagation()}
            // `id` is a default, not an override: every instance of this shared button used to get
            // the literal id "action-button-icon" no matter how many rendered on one page (invalid,
            // duplicate DOM ids), which forced BugBug tests to select it by numeric position instead
            // of a stable identifier. Spreading `baseIconProps` after `id` lets a caller override it
            // (or pass `data-testid`) while callers that pass nothing keep the old id unchanged.
            id="action-button-icon"
            {...baseIconProps}
          >
            <MoreHorizIcon />
          </IconButton>
        )
      }
    >
      {actions &&
        actions.map((action, index) => {
          if (action.key === 'divider') {
            return (
              <MenuItem
                key={`${action}-${index}`}
                disabled
                classes={{ root: classnames(classes.divider, { [classes.dividerNext]: nextMenu }) }}
              />
            )
          }

          return (
            <MenuItem
              // `menuItem` top-aligns for the two-line label+sublabel form; the next-menu rows are
              // single-line and centre, so the two never both apply (they set the same property).
              className={classnames({
                [classes.menuItem]: !nextMenu,
                [nextClasses.item]: !!nextMenu,
                [classes.scary]: action.scary
              })}
              key={action.key}
              icon={action.icon}
              onClick={(ev) => {
                onActionClick(ev, action.key, action.payload)
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                {action.label}
                {action.sublabel && (
                  <Typography variant="caption" color="textSecondary">
                    {action.sublabel}
                  </Typography>
                )}
              </div>
            </MenuItem>
          )
        })}
    </PopupMenu>
  )
}

export default ActionButton
